import type { DestinationModelSpec } from "@elara/domain/models/DestinationModelSpec";
import type { DailyForecast, GetWeatherResponse } from "@elara/domain/models/schemas/weather-schemas";

import type { DestinationsContainer } from "#containers/DestinationsContainer.js";
import type { ClientDocumentHandle } from "#firebase/types.js";
import type { AppFlowsContainer } from "#flows.js";
import type { Destination } from "#models/DestinationModel.js";

import { getErrorReporter } from "#lib/error-reporting.js";
import { createState } from "#lib/signals-utils.js";
import { parseDocumentId } from "#lib/utils.js";

type CurrentWeather = GetWeatherResponse['current'];

export function createDestinationGuideState(
  destinations: DestinationsContainer,
  flows: AppFlowsContainer,
) {
  const state = createState({
    handle: undefined as ClientDocumentHandle<DestinationModelSpec> | undefined,

    weather: undefined as CurrentWeather | undefined,
    forecast: undefined as DailyForecast[] | undefined,
    isWeatherLoading: false as boolean,
    weatherError: undefined as string | undefined,

    get destination(): Destination | undefined {
      return this.handle?.data as Destination | undefined;
    },

    get isLoading(): boolean {
      return !(this.handle?.isReady ?? false);
    },

    /** Finds a destination by its ID */
    findDestination(destinationId: string) {
      try {
        const id = parseDocumentId(destinationId) ?? destinationId;
        this.handle = destinations.ClientDestination.get({ id });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error;
      }
    },

    async loadWeather({
      units,
      startDate,
      endDate,
    }: { units?: 'metric' | 'imperial'; startDate?: string; endDate?: string } = {}) {
      const dest = this.destination;
      if (!dest) return;
      
      try {
        this.isWeatherLoading = true;
        this.weatherError = undefined;

        // Build payload with proper typing to avoid type assertions
        const payload: Parameters<typeof flows.getDestinationWeather>[0] = {
          destinationId: dest.id,
          name: dest.name,
          countryCode: dest.location?.countryCode,
          units,
          startDate,
          endDate,
        };

        // Only add stateCode if it exists and is valid (not null/undefined)
        if (dest.location?.stateProvinceCode !== undefined && dest.location?.stateProvinceCode !== null) {
          payload.stateCode = dest.location.stateProvinceCode;
        }

        const result = await flows.getDestinationWeather(payload);
         
        const data = result.data as GetWeatherResponse;

        this.weather = data.current;
        this.forecast = data.forecast;
        this.weatherError = undefined;
      } catch (error) {
        getErrorReporter().captureException(error);
        this.weatherError = error instanceof Error ? error.message : 'Failed to load weather';
      } finally {
        this.isWeatherLoading = false;
      }
    },

    /** Resets the state */
    reset() {
      this.handle = undefined;
      this.weather = undefined;
      this.forecast = undefined;
      this.isWeatherLoading = false;
      this.weatherError = undefined;
    }
  });

  return state;
}

/** The main reactive store for all core destination guide functionality. All properties are signals transparent to the user. */
export type DestinationGuideState = ReturnType<typeof createDestinationGuideState>;
